import CertificateLabel from '@/components/elements/course/general/CertificateLabel';
import {
  OutlinedArrowLeftMD,
  OutlinedCheck,
} from '@/components/elements/svgIcon/outlined';
import { LessonCourseDefaultThumbnail } from '@/constant/imgImport';
import useParagraphLineClamped from '@/hooks/useLineClamped';
import { CourseCertificateEnum } from '@/interfaces/training.interface';
import { Button } from '@mui/material';
import { convertQuantity } from 'common/quantityHelpers';
import { PATH_NAME } from 'configs/pathName';
import Typography from 'design-system/components/Typography/Typography';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ComponentPropsWithoutRef } from 'react';
import EnterpriseUserList from '../client/general/EnterpriseUserList';

export interface IEnterpriseCourseCard extends ComponentPropsWithoutRef<'div'> {
  title: string;
  thumbnail: string;
  description: string;
  memberList: Array<string>;
  memberCount: number;
  courseId: number | string;
  groupId: number | string;
  certificateShortName: CourseCertificateEnum | null;
  onSelected?: () => void;
  selected?: boolean;
}

const EnterpriseCourseCard = ({
  thumbnail,
  title,
  description,
  certificateShortName,
  courseId: cid,
  memberList,
  selected,
  onSelected,
  memberCount,
  ...restProps
}: IEnterpriseCourseCard) => {
  const { contentRef, maxHeight, isClamped } = useParagraphLineClamped({
    linesAllowed: 2,
  });

  const router = useRouter();
  const redirectToCourseDetailPage = () =>
    router.push({
      pathname: PATH_NAME.COURSE_DETAIL,
      query: {
        cid,
      },
    });

  return (
    <div
      className={[
        'flex w-[260px] flex-col overflow-hidden rounded-lg bg-Other/White shadow-elevator2',
        onSelected ? 'h-auto' : 'h-full',
        onSelected
          ? selected
            ? 'shadow-[rgba(0,0,0,0.35)_0_5px_15px] outline outline-[3px] outline-Primary/Main'
            : 'outline outline-Other/Outlinedborder-stroke'
          : '',
        restProps.className,
      ].join(' ')}
    >
      <div className="relative h-[160px] flex-shrink-0">
        <Image
          layout="fill"
          onClick={onSelected || redirectToCourseDetailPage}
          src={thumbnail || LessonCourseDefaultThumbnail}
          alt="demo highlight course thumbnail"
          className="aspect-[1.625] h-[160px] cursor-pointer rounded-tl-lg rounded-tr-lg object-cover"
          loading="lazy"
          decoding="async"
        />
        {certificateShortName && (
          <div className="absolute bottom-2 left-2">
            <CertificateLabel
              certificateShortName={certificateShortName}
              className="w-fit"
            />
          </div>
        )}
        {selected && (
          <div className="absolute right-2 top-2">
            <Button
              variant="contained"
              className="!min-h-[32px] !min-w-[32px] !rounded-full !bg-Primary/Main !p-0 !shadow-elevator2"
              onClick={onSelected}
            >
              <OutlinedCheck className="!h-[16px] !w-[16px] !text-white" />
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-grow flex-col justify-between gap-4 p-3">
        <div className="flex flex-1 flex-col gap-3">
          {!onSelected && (
            <div className="flex flex-shrink-0 justify-start gap-2 md:items-center">
              {memberList.length > 0 && (
                <div>
                  <EnterpriseUserList avatarList={memberList.slice(0, 5)} />
                </div>
              )}
              <p className="text-sm font-medium leading-[120%] text-Text/Lightbackground/Secondary ">
                {convertQuantity(memberCount, true)} học viên
              </p>
            </div>
          )}
          <div className="flex-1">
            <Typography
              onClick={!onSelected ? redirectToCourseDetailPage : undefined}
              variant="heading4"
              className="line-clamp-2 leading-[inherit]"
            >
              {title}
            </Typography>
            {!onSelected && (
              <div className="mt-1 text-xs font-medium leading-[140%] ">
                <p
                  ref={contentRef}
                  style={{
                    maxHeight: maxHeight,
                    overflowY: 'hidden',
                  }}
                  className="text-xs font-medium leading-[140%] text-Text/Lightbackground/Secondary"
                >
                  {description}
                </p>
                {isClamped && (
                  <>
                    <span className="inline">... </span>
                    <span
                      onClick={redirectToCourseDetailPage}
                      className="inline cursor-pointer text-Primary/Main hover:underline"
                    >
                      Xem thêm
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {!onSelected && (
          <div>
            <Button
              onClick={redirectToCourseDetailPage}
              variant="medium_emphasis_v2"
            >
              Chi tiết
              <OutlinedArrowLeftMD />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterpriseCourseCard;
